import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, PartyPopper, KeyRound } from "lucide-react";

// Shared landing page for both flows that end with a Supabase magic-link session and
// need the user to set a password: (1) household email invites (redirectTo=/accept-invite,
// carries user_metadata.invited_household_id — see POST /household/invite in server.py)
// and (2) "forgot password" recovery (redirectTo=/reset-password, from resetPassword()
// in AuthContext.jsx). Supabase's client SDK auto-detects the session from the URL on
// load, so by the time this renders there's already an active session — we just need
// to collect a new password via supabase.auth.updateUser().
export default function SetPassword() {
    const navigate = useNavigate();
    const [checking, setChecking] = useState(true);
    const [hasSession, setHasSession] = useState(false);
    const [isInvite, setIsInvite] = useState(false);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let mounted = true;
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (!mounted) return;
            setHasSession(!!session);
            setIsInvite(!!session?.user?.user_metadata?.invited_household_id);
            setChecking(false);
        });
        return () => { mounted = false; };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error("Password must be 6+ characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        try {
            setBusy(true);
            const { error } = await supabase.auth.updateUser({ password });
            if (error) throw error;
            toast.success(isInvite ? "You're in! Welcome to the household." : "Password updated");
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.message || "Failed to set password");
        } finally { setBusy(false); }
    };

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-pulse text-slate-500 text-sm">Loading…</div>
            </div>
        );
    }

    if (!hasSession) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
                <div className="max-w-md w-full card-elev rounded-3xl p-10 text-center space-y-4">
                    <h1 className="font-display text-2xl font-extrabold text-slate-900">Link expired</h1>
                    <p className="text-slate-500 text-sm">This link is no longer valid. Ask for a new invite or reset link and try again.</p>
                    <Link to="/login" className="inline-block text-indigo-600 hover:text-indigo-700 font-semibold text-sm">Back to sign in</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
            <div className="max-w-md w-full card-elev rounded-3xl p-10 space-y-6">
                <div className="text-center space-y-2">
                    <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 grid place-items-center mx-auto">
                        {isInvite ? <PartyPopper className="w-7 h-7" /> : <KeyRound className="w-7 h-7" />}
                    </div>
                    <h1 className="font-display text-2xl font-extrabold text-slate-900">
                        {isInvite ? "You're invited!" : "Set a new password"}
                    </h1>
                    <p className="text-slate-500 text-sm leading-relaxed">
                        {isInvite
                            ? "You've been added to a shared household on BudgetWise. Set a password to finish setting up your account."
                            : "Choose a new password for your account."}
                    </p>
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="set-password-form">
                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-slate-700">New password</Label>
                        <div className="relative">
                            <Input data-testid="sp-password" type={show ? "text" : "password"} autoComplete="new-password"
                                value={password} onChange={e => setPassword(e.target.value)}
                                placeholder="6+ characters" className="h-12 rounded-xl pr-11" />
                            <button type="button" onClick={() => setShow(!show)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-slate-700">Confirm password</Label>
                        <Input data-testid="sp-confirm" type={show ? "text" : "password"} autoComplete="new-password"
                            value={confirm} onChange={e => setConfirm(e.target.value)}
                            placeholder="Re-enter password" className="h-12 rounded-xl" />
                    </div>
                    <Button type="submit" disabled={busy} data-testid="sp-submit"
                        className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold">
                        {busy ? "Saving…" : isInvite ? "Join household" : "Update password"}
                    </Button>
                </form>
            </div>
        </div>
    );
}
